<template>
    <div class="litigation-overview">
        <!-- Page Header -->
        <div class="page-head">
            <h2 class="title">Litigation Overview</h2>
            <p class="sub">รายการสรุปคดีความทั้งหมด — เน้นการติดตามสถานะการออก Invoice</p>
        </div>

        <!-- ============ STAT CARDS ============ -->
        <div class="stats">
            <div class="stat" :class="[stat.cls, { active: activeCard === stat.key }]" v-for="(stat, i) in stats"
                :key="stat.key" @click="onCardClick(stat.key)">
                <div class="n">{{ stat.value }}</div>
                <div class="l">{{ stat.label }}</div>
                <div class="bar"></div>
            </div>
        </div>

        <!-- ============ TOOLBAR / FILTER ============ -->
        <div class="toolbar">
            <!-- แถวบน: ค้นหา + ปุ่ม -->
            <div class="toolbar-row">
                <v-text-field v-model="search" placeholder="🔍 ค้นหา: รหัสคดี / โจทก์ / จำเลย / เลขคดีดำ-แดง" outlined
                    dense hide-details class="search" clearable />
                <v-select v-model="filter.court" :items="courtFilterList" item-text="name" item-value="name"
                    placeholder="ศาล: ทั้งหมด" outlined dense hide-details class="filter-select" />
                <v-select v-model="filter.caseType" :items="caseTypeFilterList" item-text="name" item-value="name"
                    placeholder="ประเภทคดี: ทั้งหมด" outlined dense hide-details class="filter-select" />

                <v-select v-model="filter.caseStatus" :items="caseStatusFilterList" item-text="descriptions"
                    item-value="descriptions" placeholder="สถานะคดี: ทั้งหมด" outlined dense hide-details
                    class="filter-select" />
                <button class="btn primary" @click="onNewCase">+ New Case</button>

                <!-- <v-select v-model="filter.invoiceStatus" :items="invoiceFilterList" placeholder="Invoice: ทั้งหมด"
                    outlined dense hide-details class="filter-select" /> -->
            </div>
        </div>

        <!-- ============ DATA TABLE ============ -->
        <!-- ============ ROWS PER PAGE ============ -->
        <div class="rows-per-page">
            <span>แสดง</span>
            <v-select v-model="itemsPerPage" :items="itemsPerPageOptions" outlined dense hide-details class="rpp-select"
                @change="page = 1" />
            <span>แถวต่อหน้า</span>
        </div>

        <v-data-table :headers="headers" :items="filteredItems" :items-per-page="itemsPerPage" :page.sync="page"
            class="overview-table" outlined hide-default-footer @page-count="pageCount = $event">

            <!-- รหัสคดี -->
            <template #[`item.caseNo`]="{ item }">
                <a v-if="item.caseNo" class="link" @click="onView(item)">{{ item.caseNo }}</a>
                <span v-else class="hint">— ไม่มีรหัสคดี</span>
            </template>

            <!-- โจทก์ / จำเลย -->
            <template #[`item.parties`]="{ item }">
                <div class="party-name" v-if="item.plaintiff">{{ item.plaintiff }}</div>
                <div class="hint" v-else>— ยังไม่ระบุโจทก์</div>
                <span class="hint party-name" v-if="item.defendant">v. {{ item.defendant }}</span>
                <span class="hint" v-else>— ยังไม่ระบุจำเลย</span>
            </template>

            <!-- ศาล (คอลัมน์นี้ไม่มี template เดิม — ถ้าอยากให้ว่างแล้วโชว์ placeholder ต้องเพิ่ม) -->
            <template #[`item.court`]="{ item }">
                <template v-if="item.court">{{ item.court }}</template>
                <span v-else class="hint">— ยังไม่ระบุศาล</span>
            </template>

            <!-- เลขดำ / แดง -->
            <template #[`item.caseNumbers`]="{ item }">
                <div v-if="item.blackNo">{{ item.blackNo }}</div>
                <div v-else class="hint">— ยังไม่มีเลขดำ</div>
                <span class="hint">{{ item.redNo || '— ยังไม่มีเลขแดง' }}</span>
            </template>

            <!-- นัดถัดไป (เดิมมี v-else อยู่แล้ว) -->
            <template #[`item.nextHearing`]="{ item }">
                <template v-if="item.nextHearingDate">
                    <div :class="{ 'danger-text': item.isUrgent }">{{ item.nextHearingDate }}</div>
                    <span class="hint" :class="{ 'danger-text': item.isUrgent }">{{ item.nextHearingTopic }}</span>
                </template>
                <span v-else class="hint">— ยังไม่มีนัด</span>
            </template>

            <!-- สถานะคดี -->
            <template #[`item.caseStatus`]="{ item }">
                <span v-if="item.caseStatus" class="cs">{{ item.caseStatus }}</span>
                <span v-else class="hint">— ยังไม่ระบุสถานะ</span>
            </template>

            <!-- Price (เดิมถูกต้องแล้ว) -->
            <template #[`item.price`]="{ item }">
                <template v-if="item.price">฿{{ formatNumber(item.price) }}</template>
                <span v-else class="hint">— ยังไม่ตั้งราคา</span>
            </template>

            <!-- Invoice Status -->
            <template #[`item.soid`]="{ item }">
                <span v-if="item.soid" class="pill">
                    {{ item.soid }}
                </span>
                <span v-else class="hint">— ยังไม่ออกระบุใบเสนอราคา</span>
            </template>
            <!-- Invoice No -->
            <template #[`item.invoiceNo`]="{ item }">
                <template v-if="item.invoiceNo">
                    <div class="pill" v-for="(no, idx) in item.invoiceNo.split(',')" :key="idx">
                        {{ no.trim() }}
                    </div>
                </template>
                <span v-else class="hint">— ยังไม่ออก invoice</span>
            </template>

            <template #[`item.invoiceStatus`]="{ item }">
                <span v-if="item.invoiceStatus" class="pill">
                    {{ item.invoiceStatus }}
                </span>
                <span v-else class="hint">— ยังไม่ออกระบุ invoice</span>
            </template>

            <!-- Action -->
            <template #[`item.action`]="{ item }">
                <div class="acts">
                    <v-btn icon small class="ic" title="ดู" @click="onView(item)">
                        <v-icon small>mdi-eye-outline</v-icon>
                    </v-btn>
                    <v-btn icon small class="ic" title="แก้ไข" @click="onEdit(item)">
                        <v-icon small>mdi-pencil-outline</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>

        <!-- ============ PAGINATION ============ -->


        <div class="pager">
            <span @click="prevPage">‹</span>
            <span v-for="p in pageCount" :key="p" :class="{ on: p === page }" @click="page = p">{{ p }}</span>
            <span @click="nextPage">›</span>
        </div>

    </div>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import Hashids from "hashids";

export default {
    name: "LitigationOverview",
    data() {
        return {
            userId: null,
            authorize_id: null,
            authorize_view: false,
            authorize_add: false,
            authorize_edit: false,
            authorize_del: false,

            search: "",
            page: 1,
            pageCount: 1,
            itemsPerPage: 10,
            itemsPerPageOptions: [5, 10, 25, 50, 100],
            filter: {
                court: null,
                caseType: null,
                caseStatus: null,
                invoiceStatus: null,
            },

            // ----- stat cards -----
            // stats: [
            //     { value: "128", label: "คดีทั้งหมด", cls: "s1" },
            //     { value: "43", label: "กำลังดำเนินการ", cls: "s2" },
            //     { value: "17", label: "รอออก Invoice", cls: "s3" },
            //     { value: "฿2.4M", label: "ชำระแล้ว (เดือนนี้)", cls: "s4" },
            //     { value: "6", label: "มีนัดภายใน 7 วัน", cls: "s5" },
            // ],

            // ----- filter options -----
            courtFilterList: [],
            caseTypeFilterList: [],
            caseStatusFilterList: [],
            invoiceFilterList: [],

            // ----- table headers -----
            headers: [
                { text: "รหัสคดี", value: "caseNo", sortable: true },
                { text: "โจทก์ / จำเลย", value: "parties", sortable: false },
                { text: "ศาล", value: "court", sortable: true },
                { text: "เลขดำ / แดง", value: "caseNumbers", sortable: false },
                { text: "นัดถัดไป", value: "nextHearing", sortable: false },
                { text: "สถานะคดี", value: "caseStatus", sortable: true },
                { text: "เลขที่ใบเสนอราคา", value: "soid", sortable: true },
                { text: "Price", value: "price", sortable: true },
                { text: "เลขที่ Invoice", value: "invoiceNo", sortable: true },
                { text: "สถานะ Invoice", value: "invoiceStatus", sortable: true },
                { text: "Action", value: "action", sortable: false, align: "center" },
            ],
            activeCard: null,
            // ----- table data (mock จาก wireframe — เปลี่ยนเป็น API จริงภายหลัง) -----
            items: [],
        };
    },
    watch: {
        search() {
            this.page = 1;
        },
    },
    computed: {
        filteredItems() {
            const kw = (this.search || "").trim().toLowerCase();

            return this.items.filter((item) => {
                const searchOk =
                    !kw ||
                    [item.caseNo, item.plaintiff, item.defendant, item.blackNo, item.redNo]
                        .some((f) => (f || "").toString().toLowerCase().includes(kw));

                // ----- กรองตามการ์ดที่กด -----
                let cardOk = true;
                if (this.activeCard === "progress") {
                    cardOk = item.caseStatus !== "ปิดคดี";
                } else if (this.activeCard === "wait_inv") {
                    cardOk = item.invoiceStatus === "Not Invoice";
                } else if (this.activeCard === "urgent") {
                    cardOk = this.isWithinDays(item.nextHearingDateRaw, 7);
                }
                // "all" หรือ null = ไม่กรอง

                const courtOk =
                    !this.filter.court || this.filter.court.includes("ทั้งหมด") || item.court === this.filter.court;
                const caseTypeOk =
                    !this.filter.caseType || this.filter.caseType.includes("ทั้งหมด") || item.caseType === this.filter.caseType;
                const caseStatusOk =
                    !this.filter.caseStatus || this.filter.caseStatus.includes("ทั้งหมด") || item.caseStatus === this.filter.caseStatus;
                const invoiceOk =
                    !this.filter.invoiceStatus || this.filter.invoiceStatus.includes("ทั้งหมด") || item.invoiceStatus === this.filter.invoiceStatus;

                return searchOk && cardOk && courtOk && caseTypeOk && caseStatusOk && invoiceOk;
            });
        },
        stats() {
            const items = this.items;
            const total = items.length;
            const inProgress = items.filter((i) => i.caseStatus !== "ปิดคดี").length;
            const waitInvoice = items.filter((i) => i.invoiceStatus === "Not Invoice").length;
            const within7 = items.filter((i) => this.isWithinDays(i.nextHearingDateRaw, 7)).length;

            return [
                { key: "all", value: total, label: "คดีทั้งหมด", cls: "s1" },
                { key: "progress", value: inProgress, label: "กำลังดำเนินการ", cls: "s2" },
                { key: "wait_inv", value: waitInvoice, label: "รอออก Invoice", cls: "s3" },
                { key: "urgent", value: within7, label: "มีนัดภายใน 7 วัน", cls: "s5" },
            ];
        },
    },

    async beforeCreate() {
        this.$store.state.navMenu = true;
    },

    async mounted() {
        try {
            await api.checkVersion();

            this.userId = localStorage.getItem(server.USER_ID);
            this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

            // ----------------- Check Authorize ID ---------------------------
            if (this.authorize_id == null || this.authorize_id == 0) {
                this.$store.state.global_dialog = true;
                this.setupAlertDialog(
                    true,
                    "Authorize Failed!!!",
                    "Please Logout And Login Again!!!",
                    "text-h5 red--text text-center"
                );
                this.$router.back();
                return;
            }

            // ----------------- Resolve Current Route Path -------------------
            const parser = document.createElement("a");
            parser.href = window.location;
            const routerPath = parser.pathname.split("/")[1];

            // ----------------- Fetch Authorization --------------------------
            const resAuth = await api.getAuthorize(this.userId, routerPath);
            const permission = resAuth.data[0];

            if (!permission) {
                this.$router.back();
                return;
            }

            this.authorize_view = permission.smd_view >= 1;
            this.authorize_add = permission.smd_add >= 1;
            this.authorize_edit = permission.smd_edit >= 1;
            this.authorize_del = permission.smd_del >= 1;

            if (!this.authorize_view) {
                this.$router.back();
                return;
            }

            // ----------------- Check Login Status ---------------------------
            if (this.userId && api.isLoggedIn()) {
                // already logged in
                await this.loaddataLitigationCourt()
                await this.loaddataLitigationCourtCasetype()
                await this.loaddataLitigationCourtStatus()
                await this.loaddata()
            } else {
                this.$store.state.isLogged = false;
                this.$router.push("/login");
                return;
            }


        } catch (error) {
            console.error("LitigationOverview mounted() error:", error);
            this.$store.state.isLogged = false;
            this.$router.push("/login");
        } finally {
            this.$hideLoader();
        }
    },

    methods: {
        async loaddataLitigationCourt() {
            try {
                const res = await api.dispute_resolution_getall_court();
                this.courtFilterList = [
                    { id: null, name: "ศาล: ทั้งหมด" },
                    ...(res.data || []),
                ];
            } catch (error) {
                console.error("loaddataLitigationCourt error:", error);
            }
        },

        async loaddataLitigationCourtCasetype() {
            try {
                const res = await api.dispute_resolution_getall_courttype();
                this.caseTypeFilterList = [
                    { id: null, name: "ประเภทคดี: ทั้งหมด" },
                    ...(res.data || []),
                ];

            } catch (error) {
                console.error("loaddataLitigationCourtCasetype error:", error);
            }
        },
        async loaddataLitigationCourtStatus() {
            const result = await api.getClientTimelineStatusCongFigByModule("Dispute Resolution");

            this.caseStatusFilterList = (result.data || []).map(item => ({
                ...item,
                module_id: Number(item.module_id)
            }));

        },
        setupAlertDialog(status, title, message, classText) {
            this.$store.state.global_dialog_message = {
                status: status,
                title: title,
                message: message,
                class: classText,
            };
        },

        // เช็คว่า dateStr อยู่ในช่วง [วันนี้, วันนี้+days] ไหม (เลยมาแล้วไม่นับ)
        isWithinDays(dateStr, days) {
            if (!dateStr) return false;
            const d = new Date(dateStr);
            if (isNaN(d)) return false;

            // ตัดเวลาออก เทียบเฉพาะวันที่
            d.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const limit = new Date(today);
            limit.setDate(limit.getDate() + days);

            return d >= today && d <= limit;
        },

        // เช็คว่าวันที่ที่ส่งมา ตรงกับวันนี้ไหม
        isSameDay(dateStr) {
            if (!dateStr) return false;
            const d = new Date(dateStr);
            if (isNaN(d)) return false;
            const now = new Date();
            return (
                d.getFullYear() === now.getFullYear() &&
                d.getMonth() === now.getMonth() &&
                d.getDate() === now.getDate()
            );
        },

        // แปลงเป็นรูปแบบไทยย่อ เช่น "18 มิ.ย. 69"
        formatThaiDate(dateStr) {
            if (!dateStr) return "";
            const d = new Date(dateStr);
            if (isNaN(d)) return dateStr;
            const months = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
                "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
            const day = d.getDate();
            const month = months[d.getMonth()];
            const yearBE = (d.getFullYear() + 543).toString().slice(-2);  // ปี พ.ศ. 2 หลักท้าย
            return `${day} ${month} ${yearBE}`;
        },
        formatNumber(n) {
            return Number(n).toLocaleString("th-TH");
        },
        onCardClick(key) {
            // กดการ์ดเดิมซ้ำ = ยกเลิก (toggle)
            if (this.activeCard === key) {
                this.activeCard = null;
                return;
            }
            this.activeCard = key;
            this.page = 1;
        },
        invoicePillClass(status) {
            switch (status) {
                case "Draft":
                    return "p-draft";
                case "Requested":
                    return "p-req";
                case "Invoiced":
                    return "p-inv";
                case "Paid":
                    return "p-paid";
                default:
                    return "p-draft";
            }
        },

        onNewCase() {
            this.$router.push("/dispute-resolution-docket/new");
        },

        prevPage() {
            if (this.page > 1) this.page--;
        },

        nextPage() {
            if (this.page < this.pageCount) this.page++;
        },

        onView(item) {
            var mainid = `${item.id}`;
            const hashids = new Hashids("my-secret-salt", 8);
            const encoded = hashids.encode(mainid);
            var encodedid = encoded;
            var module_name_send = "Dispute Resolution"
            const localUrl = window.location.origin;
            const text = `${localUrl}/client-view/case/${encodedid}?module_name=${encodeURIComponent(module_name_send)}`;
            window.open(text, "_blank");
        },

        onEdit(item) {
            this.$router.push(`/dispute-resolution-docket/${item.id || item.caseNo}`);
        },

        // ----- ตัวอย่าง method โหลดข้อมูลจริง -----
        async loaddata() {
            try {
                const res = await api.dispute_resolution_get_forDataTable(
                    Number(localStorage.getItem(server.COMPANYID))
                );
                console.log(res)
                const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);

                this.items = rows.map((r) => {
                    const hearingDate = r.next_hearing_date || "";   // ค่าดิบจาก API เช่น "2026-06-18"
                    const isToday = this.isSameDay(hearingDate);

                    let topic = r.next_hearing_topic || "";
                    if (isToday && topic) {
                        topic = "วันนี้ · " + topic;   // นัดวันนี้ → เติมข้อความนำหน้า
                    } else if (isToday) {
                        topic = "วันนี้";
                    }

                    return {
                        id: r.id,
                        caseNo: r.internal_case_no || "",
                        plaintiff: r.plaintiffs || "",
                        defendant: r.defendants || "",
                        court: r.court || "",
                        blackNo: r.black_no || "",
                        redNo: r.red_no || "",
                        caseStatus: r.case_status || "",
                        nextHearingDateRaw: hearingDate,
                        nextHearingDate: hearingDate ? this.formatThaiDate(hearingDate) : "",
                        nextHearingTopic: topic,
                        isUrgent: isToday,
                        price: r.soprice,
                        invoiceStatus: r.invoice_status,
                        invoiceNo: r.invoice_no,
                        soid: r.soid,
                        caseType: r.casename
                    };
                });
                // console.log(this.items)
            } catch (error) {
                console.error("loaddata error:", error);
                this.items = [];
            }
        },
    },
};
</script>

<style scoped>
/* ===== ต้นฉบับ wireframe : ตัวแปรสี ===== */
.litigation-overview {
    --navy: #0e3a5f;
    --navy2: #15507f;
    --teal: #1b7a99;
    --line: #d4d9e0;
    --ink: #1f2733;
    --muted: #6b7785;
    --bg: #f4f6f9;
    --card: #ffffff;
    --chip: #eef2f7;

    max-width: 1180px;
    margin: 0 auto;
    padding: 22px;
    /* background: var(--bg); */
    color: var(--ink);
    font-family: "Segoe UI", Tahoma, Arial, sans-serif;
    font-size: 14px;
}

/* ===== header ===== */
.title {
    font-size: 20px;
    margin: 0 0 4px;
}

.sub {
    color: var(--muted);
    margin: 0 0 18px;
}

/* ===== stat cards (grid 5 คอลัมน์ ตรงต้นฉบับ) ===== */
.stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 35px;
    margin-bottom: 20px;
}

.stat {
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 14px;
}

.stat .n {
    font-size: 24px;
    font-weight: 700;
}

.stat .l {
    color: var(--muted);
    font-size: 12px;
    margin-top: 2px;
}

.stat .bar {
    height: 3px;
    border-radius: 3px;
    margin-top: 10px;
    background: var(--navy);
}

.stat.s2 .bar {
    background: #2563eb;
}

.stat.s3 .bar {
    background: #c47f00;
}

.stat.s4 .bar {
    background: #1f8f4e;
}

.stat.s5 .bar {
    background: #c0392b;
}

/* ===== toolbar ===== */
.toolbar {
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 14px;
}

.toolbar-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
}

.toolbar-row+.toolbar-row {
    margin-top: 10px;
    /* ระยะห่างระหว่างแถวบน-ล่าง */
}

.toolbar .search {
    flex: 1;
    /* ช่องค้นหายืดเต็มแถวบน เหลือที่ให้ปุ่ม */
    max-width: 350px;
}

.toolbar .search {
    flex: 0 1 350px;
    /* ช่องค้นหา กว้างสุด ~350px ไม่ยืดเต็มแถว */
    max-width: 350px;
}

/* ปุ่มถูกดันไปขวาสุด */
.toolbar-row .btn.primary {
    margin-left: auto;
}

/* บีบความสูง v-text-field / v-select ให้เท่าต้นฉบับ (input padding 8-10px) */
.toolbar .v-input {
    font-size: 13px;
    flex: 0 0 auto;
}

.toolbar .search.v-input {
    flex: 1 1 200px;
}

.filter-select {
    min-width: 150px;
    max-width: 200px;
}

.toolbar>>>.v-input__control>.v-input__slot {
    min-height: 38px !important;
    border-radius: 6px !important;
    background: #fff !important;
}

.toolbar>>>fieldset {
    border-color: var(--line) !important;
}

.toolbar>>>.v-text-field--outlined .v-input__slot {
    padding: 0 10px !important;
}

/* ===== ปุ่ม (button ตรงต้นฉบับ) ===== */
.btn {
    border: none;
    border-radius: 6px;
    padding: 9px 16px;
    font-size: 13px;
    cursor: pointer;
}

.btn.primary {
    background: var(--navy);
    color: #fff;
}

/* ===== table (v-data-table override ให้เหมือน <table> ต้นฉบับ) ===== */
.overview-table {
    background: #fff;
    border: 1px solid var(--line);
    border-radius: 10px;
    overflow: hidden;
}

.overview-table>>>table {
    border-collapse: collapse;
}

.overview-table>>>thead th {
    background: #eef2f7 !important;
    color: var(--muted) !important;
    font-weight: 600 !important;
    text-transform: uppercase;
    font-size: 11px !important;
    letter-spacing: .5px;
    height: auto !important;
    padding: 11px 12px !important;
    border-bottom: 1px solid var(--line) !important;
}

.overview-table>>>tbody td {
    font-size: 13px !important;
    padding: 11px 12px !important;
    border-bottom: 1px solid var(--line) !important;
    height: auto !important;
}

.overview-table>>>tbody tr:last-child td {
    border-bottom: none !important;
}

.overview-table>>>tbody tr:hover td {
    background: #f9fbfd !important;
}

.hint {
    font-size: 11px;
    color: var(--muted);
}

.danger-text {
    color: #c0392b !important;
}

.link {
    color: var(--navy2);
    text-decoration: none;
    font-weight: 600;
    cursor: pointer;
}

/* ===== chip สถานะคดี ===== */
.cs {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 11.5px;
    background: var(--chip);
    color: #415061;
}

/* ===== pill invoice ===== */
.pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;
}

.p-draft {
    background: #eceff3;
    color: #5a6573;
}

.p-req {
    background: #fff2d6;
    color: #9a6a00;
}

.p-inv {
    background: #e3edff;
    color: #2150c0;
}

.p-paid {
    background: #dff3e6;
    color: #1f7a44;
}

/* ===== action buttons ===== */
.acts {
    display: flex;
    gap: 6px;
}

.ic {
    width: 30px;
    height: 30px;
    border: 1px solid var(--line) !important;
    border-radius: 6px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 13px;
}

/* ===== pager (box style ตรงต้นฉบับ) ===== */
.pager {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 12px;
}

.pager span {
    padding: 6px 11px;
    border: 1px solid var(--line);
    border-radius: 6px;
    background: #fff;
    font-size: 12px;
    cursor: pointer;
}

.pager span.on {
    background: var(--navy);
    color: #fff;
}

.rows-per-page {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 10px;
    margin-top: 20px;
}

.table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
}

.rows-per-page {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--muted);
}



.pager {
    display: flex;
    gap: 6px;
    /* margin-top ลบออก — footer คุมแล้ว */
}

.rpp-select {
    max-width: 80px;
    flex: 0 0 auto;
}

.rpp-select>>>.v-input__control>.v-input__slot {
    min-height: 28px !important;
    height: 28px !important;
}

.rpp-select>>>fieldset {
    height: 32px;
}

.rpp-select>>>.v-select__selections {
    min-height: 28px !important;
    padding: 0 !important;
}

.rpp-select>>>.v-input__append-inner {
    margin-top: 2px !important;
}

.rpp-select>>>.v-select__selection {
    font-size: 12px;
}

.stat {
    cursor: pointer;
    transition: box-shadow .15s, border-color .15s;
}

.stat:hover {
    border-color: var(--navy2);
}

.stat.active {
    border-color: var(--navy);
    box-shadow: 0 0 0 2px rgba(14, 58, 95, .15);
}

.party-name {
    white-space: pre-line;
}
</style>